import React, { memo, MouseEventHandler } from 'react';
import styled from 'styled-components';
import { isInteger } from 'lodash';

import Count from '../../Common/Count';
import TimeFrom from '../../Common/TimeFrom';

import Messenger, { Types } from '../Messenger';
import { MESSAGE_STATUS } from '../../../../constants';

import { Read, Received, Sent, CreateNewConversation } from '@icons/common';
import { ValueOf } from '@utils/tsUtils';

export interface IConversationCard2 {
  className?: string;
  type?: ValueOf<typeof Types>;
  title?: string;
  time?: number;
  to?: string;
  unreadAmount?: number;
  isHasStatus?: boolean;
  messageStatus?: ValueOf<typeof MESSAGE_STATUS>;
  isCreateNewConversation?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
}

const ConversationCard2 = ({
  className = '',
  type,
  title,
  time,
  to,
  unreadAmount,
  isHasStatus = false,
  messageStatus = MESSAGE_STATUS.NONE,
  isCreateNewConversation = false,
  ...props
}: IConversationCard2) => {
  let messageStatusIcon;
  switch (messageStatus) {
    case MESSAGE_STATUS.SENT:
      messageStatusIcon = <StyledSentIcon />;
      break;
    case MESSAGE_STATUS.RECEIVED:
      messageStatusIcon = <StyledReceivedIcon />;
      break;
    case MESSAGE_STATUS.READ:
      messageStatusIcon = <StyledReadIcon />;
      break;
    default:
      messageStatusIcon = null;
  }
  let icon: null | JSX.Element = null;
  if (isCreateNewConversation) {
    icon = (
      <StyledWrapperCreateNewConversation>
        <CreateNewConversationIcon>
          {/* storybook hack */}
        </CreateNewConversationIcon>
      </StyledWrapperCreateNewConversation>
    );
  } else if (type) {
    icon = <StyledMessenger type={type} />;
  }

  const isValidCount =
    !!unreadAmount && isInteger(unreadAmount) && unreadAmount > 0;

  return (
    <StyledConversationCard2 className={className} {...props}>
      {icon}
      <StyledContainer>
        {title && <StyledTitle>{title}</StyledTitle>}
        {to && <StyledWraper>{to}</StyledWraper>}
        <StyledWraper>
          {!!time && <StyledTime time={time} />}
          {isHasStatus && messageStatusIcon}
        </StyledWraper>
      </StyledContainer>
      <StyledCountContainer>
        {isValidCount && <Count value={unreadAmount} />}
      </StyledCountContainer>
    </StyledConversationCard2>
  );
};

const StyledConversationCard2 = styled.div`
  min-height: 65px;
  display: flex;
`;

const StyledWrapperCreateNewConversation = styled.div`
  width: 24px;
  height: 18px;
  border-radius: 50%;
  overflow: hidden;
  margin-top: 8px;
  margin-right: 6px;

  > svg {
    height: 100%;
    width: 100%;
  }
`;

const StyledMessenger = styled(Messenger)`
  height: 18px;
  margin-top: 8px;
  margin-right: 6px;
  min-width: 24px;

  > svg {
    height: 100%;
    width: 100%;
  }
`;

const StyledSentIcon = styled(Sent)`
  min-width: 20px;
  height: 14px;
  margin-left: 5px;
`;

const StyledReadIcon = styled(Read)`
  min-width: 20px;
  height: 14px;
  margin-left: 5px;
`;

const StyledReceivedIcon = styled(Received)`
  min-width: 20px;
  height: 14px;
  margin-top: 2px;
  margin-left: 5px;
`;

const StyledContainer = styled.div`
  flex-grow: 1;
  line-height: 1;
  margin-top: 7px;
`;

const StyledTitle = styled.div`
  color: #4d4d4d;
  min-height: 17px;
  font-size: 14px;
  line-height: normal;
  overflow: hidden;
`;

const StyledWraper = styled.div`
  display: flex;
  flex-grow: 1;
  font-size: 12px;
  margin: 3px 0;
  color: #919499;
`;

const StyledTime = styled(TimeFrom)`
  margin-top: 2px;
  cursor: pointer;
`;

const StyledCountContainer = styled.div`
  width: 23px;
  margin-top: 5px;
  margin-left: 5px;
`;

const CreateNewConversationIcon = styled(CreateNewConversation)`
  width: 18px;
  height: 19px;
`;

export default memo(ConversationCard2);
